import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Snackbar,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  List,
  ListItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchSystemJobs,
  triggerSystemJob,
  selectSystemJobs,
  selectSystemJobsLoading,
  selectSystemJobsError,
  selectTriggeringJob,
  selectTriggerJobError,
  selectTriggerJobSuccess,
  clearTriggerError,
  clearTriggerSuccess,
  clearError,
} from "store/SystemJobsSlice";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import { supabase } from "services/supabase";

function TriggerEventModal({ open = false, onClose, initialData = null, filterPrefix = null }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { currentUser, loading: authLoading } = useSupabaseAuth();

  // Redux state
  const jobs = useSelector(selectSystemJobs);
  const loading = useSelector(selectSystemJobsLoading);
  const error = useSelector(selectSystemJobsError);
  const jobsLoaded = useSelector((state) => state.systemJobsReducer.jobsLoaded);
  const triggering = useSelector(selectTriggeringJob);
  const triggerError = useSelector(selectTriggerJobError);
  const triggerSuccess = useSelector(selectTriggerJobSuccess);

  // Filter jobs by prefix if provided
  const filteredJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];
    if (!filterPrefix) return jobs;
    return jobs.filter((job) => job.event && job.event.startsWith(filterPrefix.toUpperCase()));
  }, [jobs, filterPrefix]);

  // Local state
  const [selectedEvent, setSelectedEvent] = useState("");
  const [jsonData, setJsonData] = useState("");
  const [triggerResult, setTriggerResult] = useState(null);
  const [streamChanges, setStreamChanges] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamCompleted, setStreamCompleted] = useState(false);
  const eventSourceRef = useRef(null);

  // Load from localStorage on mount (only payload, event will be validated after jobs load)
  useEffect(() => {
    const savedPayload = localStorage.getItem('debug_trigger_payload');
    if (savedPayload) {
      setJsonData(savedPayload);
    }
  }, []);

  // Handle initialData when modal opens - don't merge, let event selection handle payload structure
  // This effect only ensures initialData is available for injection when an event is selected
  useEffect(() => {
    // When modal opens with initialData but no event selected yet, 
    // don't populate jsonData - wait for event selection to load the schema
    // The injection will happen when an event is selected via handleEventChange or the useEffect below
  }, [open, initialData]);

  // Save to localStorage whenever event or payload changes
  useEffect(() => {
    if (selectedEvent) {
      localStorage.setItem('debug_trigger_event', selectedEvent);
    } else {
      localStorage.removeItem('debug_trigger_event');
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (jsonData) {
      localStorage.setItem('debug_trigger_payload', jsonData);
    } else {
      localStorage.removeItem('debug_trigger_payload');
    }
  }, [jsonData]);

  // Load and validate saved event from localStorage when jobs are available
  useEffect(() => {
    if (filteredJobs && filteredJobs.length > 0 && !selectedEvent) {
      const savedEvent = localStorage.getItem('debug_trigger_event');
      if (savedEvent) {
        // Validate that the saved event exists in the filtered jobs
        const jobExists = filteredJobs.some((job) => job.event === savedEvent);
        if (jobExists) {
          setSelectedEvent(savedEvent);
        } else {
          // Clear invalid saved event from localStorage
          localStorage.removeItem('debug_trigger_event');
        }
      }
    }
  }, [filteredJobs, selectedEvent]);

  // Helper function to recursively inject values into payload based on field names found in the payload
  const injectValuesIntoPayload = useCallback((obj, valuesToInject) => {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => injectValuesIntoPayload(item, valuesToInject));
    }

    const result = { ...obj };

    // Check for UID variations
    if (valuesToInject.uid) {
      if ('uid' in result) result.uid = valuesToInject.uid;
      if ('userId' in result) result.userId = valuesToInject.uid;
      if ('user_id' in result) result.user_id = valuesToInject.uid;
    }

    // Check for invoiceId variations
    if (valuesToInject.invoiceId) {
      if ('invoiceId' in result) result.invoiceId = valuesToInject.invoiceId;
      if ('invoice_id' in result) result.invoice_id = valuesToInject.invoiceId;
    }

    // Check for clientId variations
    if (valuesToInject.clientId) {
      if ('clientId' in result) result.clientId = valuesToInject.clientId;
      if ('client_id' in result) result.client_id = valuesToInject.clientId;
    }

    // Recursively process nested objects
    for (const key in result) {
      if (result[key] && typeof result[key] === 'object') {
        result[key] = injectValuesIntoPayload(result[key], valuesToInject);
      }
    }

    return result;
  }, []);

  // Helper function to inject UID and initialData (invoiceId/clientId) into JSON payload
  const injectUidAndInitialDataIntoPayload = useCallback((jsonDataString) => {
    const valuesToInject = {};
    
    // Add UID if available
    if (currentUser?.id) {
      valuesToInject.uid = currentUser.id;
    }
    
    // Add invoiceId or clientId from initialData if available
    if (initialData) {
      if (initialData.invoiceId) {
        valuesToInject.invoiceId = initialData.invoiceId;
      }
      if (initialData.clientId) {
        valuesToInject.clientId = initialData.clientId;
      }
    }

    // If no values to inject, return as is
    if (Object.keys(valuesToInject).length === 0) {
      return jsonDataString;
    }

    // Handle empty or whitespace-only strings
    if (typeof jsonDataString === 'string' && !jsonDataString.trim()) {
      // For empty payload, create a base structure with the values
      return JSON.stringify(valuesToInject, null, 2);
    }

    try {
      // Parse the JSON string
      const parsed = typeof jsonDataString === 'string' 
        ? JSON.parse(jsonDataString)
        : jsonDataString;
      
      // Use the recursive injection function to find and update matching fields
      const updated = injectValuesIntoPayload(parsed, valuesToInject);
      return JSON.stringify(updated, null, 2);
    } catch (error) {
      // If parsing fails, create a new JSON object with fields
      console.warn("Failed to parse JSON, creating new object with injected fields:", error);
      return JSON.stringify(valuesToInject, null, 2);
    }
  }, [currentUser, initialData, injectValuesIntoPayload]);

  // When jobs load and we have a selected event but no payload, populate the JSON data from the job
  useEffect(() => {
    if (selectedEvent && filteredJobs && filteredJobs.length > 0 && !jsonData) {
      const selectedJob = filteredJobs.find((job) => job.event === selectedEvent);
      if (selectedJob) {
        const jobData = selectedJob.data || 
                       selectedJob.payload || 
                       selectedJob.schema ||
                       (() => {
                         const { event, description, ...rest } = selectedJob;
                         return Object.keys(rest).length > 0 ? rest : {};
                       })();
        
        let jsonString = typeof jobData === 'string' 
          ? jobData 
          : JSON.stringify(jobData, null, 2);
        
        // Inject UID and initialData (invoiceId/clientId) into the payload
        jsonString = injectUidAndInitialDataIntoPayload(jsonString);
        
        setJsonData(jsonString);
      }
    }
  }, [filteredJobs, selectedEvent, injectUidAndInitialDataIntoPayload, jsonData]);

  useEffect(() => {
    // Only fetch when modal is open, auth is ready, and jobs aren't already loaded
    if (open && !authLoading && currentUser && !jobsLoaded && !loading) {
      dispatch(fetchSystemJobs());
    }
  }, [open, authLoading, currentUser, jobsLoaded, loading, dispatch]);

  // Debug: Log jobs when they change
  useEffect(() => {
    if (jobs && jobs.length > 0) {
      console.log("System jobs loaded:", jobs);
    }
  }, [jobs]);

  const handleEventChange = (event, newValue) => {
    // Autocomplete passes (event, newValue) where newValue is the selected job object or null
    const selectedJob = newValue;
    
    if (selectedJob && selectedJob.event) {
      const eventValue = selectedJob.event;
      setSelectedEvent(eventValue);
      
      // Extract the data from the job object
      // Look for data, payload, or the entire object excluding event and description
      const jobData = selectedJob.data || 
                     selectedJob.payload || 
                     selectedJob.schema ||
                     (() => {
                       // If no explicit data property, extract all properties except event and description
                       const { event, description, ...rest } = selectedJob;
                       return Object.keys(rest).length > 0 ? rest : {};
                     })();
      
      // Convert to formatted JSON string
      let jsonString = typeof jobData === 'string' 
        ? jobData 
        : JSON.stringify(jobData, null, 2);
      
      // Inject UID and initialData (invoiceId/clientId) into the payload
      jsonString = injectUidAndInitialDataIntoPayload(jsonString);
      
      setJsonData(jsonString);
    } else {
      // Clear JSON data if no event selected
      setSelectedEvent("");
      setJsonData("");
    }
  };

  const handleTriggerJob = () => {
    if (!selectedEvent) {
      return;
    }

    // Save to localStorage before triggering
    localStorage.setItem('debug_trigger_event', selectedEvent);
    localStorage.setItem('debug_trigger_payload', jsonData);

    dispatch(triggerSystemJob({
      eventName: selectedEvent,
      data: jsonData,
    }));
  };

  // Update trigger result when triggerSuccess or triggerError changes
  useEffect(() => {
    if (triggerSuccess) {
      setTriggerResult({ type: 'success', data: triggerSuccess });
      
      // Extract streamUrl from the response
      const streamUrl = triggerSuccess.streamUrl;
      
      if (streamUrl) {
        console.log("Starting stream with URL:", streamUrl);
        startStreaming(streamUrl);
      } else {
        console.log("No streamUrl found in trigger response:", triggerSuccess);
      }
    }
  }, [triggerSuccess]);

  useEffect(() => {
    if (triggerError) {
      setTriggerResult({ type: 'error', data: triggerError });
    }
  }, [triggerError]);

  // Clear result when triggering starts
  useEffect(() => {
    if (triggering) {
      setTriggerResult(null);
      setStreamChanges([]);
      setStreamCompleted(false);
      stopStreaming();
    }
  }, [triggering]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, []);

  const startStreaming = async (streamUrl) => {
    // Stop any existing stream
    stopStreaming();

    setIsStreaming(true);
    setStreamCompleted(false);
    setStreamChanges([]);

    try {
      // Get session for auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        console.error("No authentication token available for streaming");
        setIsStreaming(false);
        return;
      }

      // Use the provided streamUrl directly from the response
      console.log("Connecting to stream URL:", streamUrl);

      // Use fetch API for SSE with custom headers (EventSource doesn't support headers)
      const response = await fetch(streamUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'text/event-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('No reader available for streaming');
      }

      // Store the abort controller for cleanup
      const abortController = new AbortController();
      eventSourceRef.current = { reader, abortController };

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log("Stream completed");
              setIsStreaming(false);
              setStreamCompleted(true);
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.trim()) {
                try {
                  // SSE format: "data: {...}"
                  const dataMatch = line.match(/^data:\s*(.+)$/m);
                  if (dataMatch) {
                    const data = JSON.parse(dataMatch[1]);
                    setStreamChanges((prev) => [...prev, {
                      id: Date.now() + Math.random(),
                      timestamp: new Date().toISOString(),
                      data: data,
                    }]);
                  } else if (line.trim().startsWith('{')) {
                    // If line is JSON, parse it directly
                    const data = JSON.parse(line.trim());
                    setStreamChanges((prev) => [...prev, {
                      id: Date.now() + Math.random(),
                      timestamp: new Date().toISOString(),
                      data: data,
                    }]);
                  }
                } catch (error) {
                  console.error("Error parsing stream message:", error);
                  setStreamChanges((prev) => [...prev, {
                    id: Date.now() + Math.random(),
                    timestamp: new Date().toISOString(),
                    data: { raw: line },
                  }]);
                }
              }
            }
          }
        } catch (error) {
          console.error("Stream reading error:", error);
          setIsStreaming(false);
          setStreamCompleted(true);
        }
      };

      processStream();

    } catch (error) {
      console.error("Error starting stream:", error);
      setIsStreaming(false);
      setStreamCompleted(true);
      setStreamChanges((prev) => [...prev, {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        data: { error: error.message },
      }]);
    }
  };

  const stopStreaming = () => {
    if (eventSourceRef.current) {
      if (eventSourceRef.current.reader) {
        eventSourceRef.current.reader.cancel();
      }
      if (eventSourceRef.current.abortController) {
        eventSourceRef.current.abortController.abort();
      }
      eventSourceRef.current = null;
    }
    if (isStreaming) {
      setStreamCompleted(true);
    }
    setIsStreaming(false);
  };

  const handleCloseSuccess = () => {
    dispatch(clearTriggerSuccess());
  };

  const handleCloseError = () => {
    dispatch(clearTriggerError());
  };

  const handleCloseModal = (event, reason) => {
    // Prevent closing on backdrop click if streaming
    if (reason === 'backdropClick' && isStreaming) {
      return;
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      PaperProps={{
        sx: {
          width: '1600px',
          maxWidth: '95vw',
          maxHeight: '70vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: 'none',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3">
            Trigger {filterPrefix ? ` (${filterPrefix.charAt(0).toUpperCase() + filterPrefix.slice(1)})` : ''} Events
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            disabled={isStreaming}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(90vh - 120px)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Left Column - Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '45%', flexShrink: 0, pr: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: '100%', overflow: 'auto' }}>
            {loading && !jobsLoaded ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 2 }}
                    onClose={() => dispatch(clearError())}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => {
                          dispatch(clearError());
                          dispatch(fetchSystemJobs());
                        }}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  This provides administrator options to trigger <b>{filterPrefix?.toUpperCase()}</b> Events. This option is only visible in debug mode.
                  There may not be any visible result.
                </Typography>
                <Autocomplete
              options={(filteredJobs || [])}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.event || '';
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.event}>
                  <Box
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.event}
                    </Typography>
                    {option.description && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ({option.description})
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              value={filteredJobs?.find((job) => job.event === selectedEvent) || null}
              onChange={handleEventChange}
              disabled={triggering || loading || !filteredJobs || filteredJobs.length === 0}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Event ${filteredJobs && filteredJobs.length > 0 ? `(${Math.min(filteredJobs.length, 5)} of ${filteredJobs.length} shown)` : ''}`}
                  variant="outlined"
                  placeholder="Search or select an event..."
                />
              )}
              isOptionEqualToValue={(option, value) => option.event === value?.event}
              PaperComponent={({ children, ...other }) => (
                <Paper
                  {...other}
                  sx={{
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {children}
                </Paper>
              )}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    padding: 0,
                  },
                },
              }}
            />
            <TextField
              label="JSON Data"
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              disabled={triggering}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                },
              }}
              placeholder='{"key": "value"}'
            />
            <Button
              variant="contained"
              onClick={handleTriggerJob}
              disabled={!selectedEvent || triggering}
              fullWidth
            >
              {triggering ? "Triggering..." : "Trigger Event"}
            </Button>
            {triggerError && (
              <Alert severity="error" onClose={handleCloseError}>
                {triggerError}
              </Alert>
            )}
              </>
            )}
            </Box>
          </Box>

          {/* Right Column - Results or Placeholder */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '55%', flexShrink: 0, pl: 1 }}>
            {triggerResult ? (
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <CardHeader 
                  title={`Trigger Results ${triggerResult.type === 'error' ? '(Error)' : '(Success)'}`}
                  subheader={
                    triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0)
                      ? (isStreaming ? "Listening for real-time updates..." : `Stream completed (${streamChanges.length} changes)`)
                      : "Response from the triggered event"
                  }
                  action={
                    triggerResult.type === 'success' && (
                      isStreaming ? (
                        <Chip 
                          label="Streaming" 
                          color="primary" 
                          size="small"
                          icon={<CircularProgress size={16} />}
                        />
                      ) : streamCompleted ? (
                        <Chip 
                          label="Stream Completed" 
                          color="success" 
                          size="small"
                        />
                      ) : null
                    )
                  }
                  sx={{
                    backgroundColor: triggerResult.type === 'error' 
                      ? 'error.light' 
                      : 'success.light',
                    py: 1.5,
                  }}
                />
                <Divider />
                <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {/* Trigger Result */}
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "grey.50",
                      borderRadius: 0,
                      flex: triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) ? 0 : 1,
                      height: triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) ? 250 : 'auto',
                      minHeight: triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) ? 350 : 500,
                      maxHeight: triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) ? 250 : 'none',
                      overflow: "auto",
                      overflowX: "auto",
                      overflowY: "auto",
                      p: 2,
                      borderBottom: triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) 
                        ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}` 
                        : 'none',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                        whiteSpace: "pre",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        margin: 0,
                        minWidth: "min-content",
                      }}
                    >
                      {typeof triggerResult.data === 'string' 
                        ? triggerResult.data 
                        : JSON.stringify(triggerResult.data, null, 2)}
                    </Typography>
                  </Paper>

                  {/* Stream Results - Only show for success with streaming */}
                  {triggerResult.type === 'success' && (isStreaming || streamChanges.length > 0) && (
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "grey.50",
                        borderRadius: 0,
                        flex: 1,
                        height: 250,
                        minHeight: 250,
                        overflow: "auto",
                        overflowX: "auto",
                        overflowY: "auto",
                        p: 2,
                      }}
                    >
                      {streamChanges.length === 0 && isStreaming ? (
                        <Box sx={{ 
                          py: 4, 
                          textAlign: "center", 
                          height: "100%",
                          display: "flex", 
                          flexDirection: "column", 
                          justifyContent: "center",
                        }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                            Waiting for stream updates...
                          </Typography>
                        </Box>
                      ) : (
                        <List sx={{ p: 0 }}>
                          {streamChanges.map((change, index) => (
                            <ListItem
                              key={change.id || index}
                              sx={{
                                borderBottom: index < streamChanges.length - 1 
                                  ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` 
                                  : 'none',
                                alignItems: "center",
                                py: 1,
                                px: 0,
                              }}
                            >
                              <Chip 
                                label={`#${index + 1}`} 
                                size="small" 
                                variant="outlined"
                                sx={{ mr: 1, flexShrink: 0 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.875rem",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  flex: 1,
                                }}
                              >
                                {change.data?.message || change.data?.msg || 
                                 (typeof change.data === 'string' ? change.data : change.data?.toString() || JSON.stringify(change.data, null, 2))}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <CardHeader 
                  title="Results"
                  subheader="Trigger results will appear here"
                  sx={{
                    backgroundColor: isDarkMode 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'grey.100',
                    py: 1.5,
                  }}
                />
                <Divider />
                <CardContent sx={{ p: 0, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ 
                    textAlign: 'center',
                    py: 8,
                    px: 4,
                    color: 'text.secondary',
                  }}>
                    <Typography variant="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                      Trigger Something, Already!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Results will appear here once you trigger an event. <br/>
                      The trigger response and any stream updates will be displayed in this panel.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} disabled={isStreaming}>
          Close
        </Button>
      </DialogActions>
      <Snackbar
        open={!!triggerSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: "100%" }}>
          Job triggered successfully!
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default TriggerEventModal;

